<template>
    <div>
        <div v-if="branch_orders_loading_status == true" class="text-center">
            <b-spinner small type="grow"></b-spinner>
            Loading orders...
        </div>
        <div v-else>
            <DashboardLayout v-if="showStats && selected_order_id === -1">
                <template #summary>
                    <SummaryStatCard
                        v-for="card in summaryCards"
                        :key="card.label"
                        :label="card.label"
                        :value="card.value"
                        :tone="card.tone"
                    />
                </template>
                <template #default>
                    <div v-if="isOrdersEmpty" class="card orders-empty-state">
                        <div class="orders-empty-state__body">
                            <p class="orders-empty-state__text">No orders for this branch yet</p>
                            <button type="button" class="btn btn-success" @click="addOrder">
                                <i class="mdi mdi-plus mr-1"></i> Add Order
                            </button>
                        </div>
                    </div>
                    <simpleLayout v-else :items_count="branch_orders.length">
                        <template #buttons>
                            <button type="button" class="btn btn-success mb-2 mr-2" @click="scanOrder()">
                                <i class="mdi mdi-barcode-scan mr-1"></i> Scan Order
                            </button>
                            <button type="button" class="btn btn-success mb-2 mr-2" @click="addOrder">
                                <i class="mdi mdi-plus mr-1"></i> Add Order
                            </button>
                            <button type="button" class="btn btn-success mb-2 mr-2" @click="exportToExcel('ordersTable')">
                                <i class="mdi mdi-file-excel mr-1"></i> Export to Excel
                            </button>
                        </template>
                        <template #content>
                            <ordersTable />
                        </template>
                    </simpleLayout>
                </template>
            </DashboardLayout>

            <orderDetails v-if="selected_order_id != -1" />

            <orderScanner @reload-event="reloadOrdersData()"></orderScanner>
        </div>
    </div>
</template>

<script>
import { orderMethods, orderComputed, adminComputed } from "@/state/helpers";
import simpleLayout from "@/components/layout-templates/simple-layout.vue";
import ordersTable from "./ordersTable.vue";
import orderDetails from "./ordersDetails.vue";
import orderScanner from './scanning/orderScanner.vue'
import DashboardLayout from "@/components/shared/DashboardLayout.vue";
import SummaryStatCard from "@/components/shared/SummaryStatCard.vue";
export default {
    data: () => ({
        ready: false,
    }),
    components: {
        simpleLayout,
        ordersTable,
        orderDetails,
        orderScanner,
        DashboardLayout,
        SummaryStatCard
    },
    watch: {
        //the queue + summary cards must reflect the selected branch (AC-7). The nav-bar branch
        //switcher only updates the store, so refetch the branch-scoped orders when it changes.
        selected_branch_id() {
            this.refreshPage();
        },
    },
    computed: {
        ...orderComputed,
        ...adminComputed,
        //will show the stats area up top if on the /orders url
        showStats() {
            if (this.$router.currentRoute.fullPath == "/orders") {
                return true;
            }
            return false;
        },
        //purposeful empty state when the selected branch has no orders. Requires a COMPLETED load
        //(=== false), not merely "not loading" — the store initializes branch_orders_loading_status
        //to null, so `!== true` would flash the empty state for a frame before the first fetch starts.
        isOrdersEmpty() {
            return this.branch_orders.length === 0 && this.branch_orders_loading_status === false;
        },
        //summary cards derived from branch_orders (branch-scoped by the server) — NOT statsOverview,
        //which the server computes without a branch filter. status.id: 1 Received, 2 Confirmed,
        //3 Processing, 4 Awaiting Collection, 5 Dispatched. Overdue mirrors the server rule
        //(due_date in the past AND not dispatched).
        summaryCards() {
            const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
            let overdue = 0;
            const now = Date.now();

            for (let i = 0; i < this.branch_orders.length; i++) {
                const order = this.branch_orders[i];
                const statusId = order.status ? order.status.id : null;
                if (counts[statusId] !== undefined) {
                    counts[statusId]++;
                }

                const notDispatched = !order.dispatched_date || order.dispatched_date === "N/A";
                if (order.due_date && notDispatched) {
                    //due_date arrives as a space-separated MySQL datetime ("YYYY-MM-DD HH:MM:SS");
                    //Safari/iOS returns Invalid Date for that, so swap the space for "T" before parsing.
                    const due = new Date(String(order.due_date).replace(" ", "T")).getTime();
                    if (!isNaN(due) && due < now) {
                        overdue++;
                    }
                }
            }

            return [
                { label: "Received", value: counts[1], tone: "neutral" },
                { label: "In Production", value: counts[2] + counts[3], tone: "neutral" },
                { label: "Awaiting Collection", value: counts[4], tone: "neutral" },
                { label: "Overdue", value: overdue, tone: "warning" },
            ];
        },
    },
    methods: {
        ...orderMethods,
        exportToExcel(id) {
            /* eslint-disable */
            return webix.toExcel($$(id), {
                ignore: { actions: true },
                filename: _.uniqueId("ordersExport_"), // for filename
                name: "Orders", // for sheet name
            });
            /* eslint-enable */
        },
        scanOrder() {
            this.$bvModal.show('scanOrderModal');
        },
        addOrder() {
            this.$router.push("/create-order");
        },
        refreshPage() {
            // calls getBranchOrders for the user's selected branch
            this.ready = false;
            this.getBranchOrders(this.selected_branch_id).then(() => {
                this.ready = true;
            });
        },
        reloadOrdersData() {
            this.ready = false;
            this.getBranchOrders(this.selected_branch_id).then(() => {
                this.ready = true;
            });
        }
    },
    created() {
        //this method will refresh the table every 5 minutes, by calling the refreshPage method which calls
        // getBranchOrders in the vue store, so the v-model for subsequent tables will auto refresh when the store is updated
        setInterval(this.refreshPage, 5 * 60 * 1000);
    },
    mounted() {
        //ensures if user navigated to another page that the id reset, else OrdersDetailPage will be rendered
        this.setSelectedOrder(-1);

        //always refetch for the selected branch on mount — a populated branch_orders cache may hold a
        //different branch's orders (navigate-away → switch branch → navigate back), which would show
        //stale data under branch scoping (AC-7).
        this.getBranchOrders(this.selected_branch_id).then(() => {
            this.ready = true;
        });
    },
    beforeDestroy() {},
};
</script>

<style lang="scss" scoped>
.orders-empty-state {
    padding: var(--space-8);
    background: var(--c-surface);
    border: 1px solid var(--c-border);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-card);
}

.orders-empty-state__body {
    text-align: center;
}

.orders-empty-state__text {
    margin-bottom: var(--space-4);
    font-size: var(--t-body);
    color: var(--c-text-muted);
}
</style>
